import React, { Suspense, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { RootState } from '../../app/store/store';
import { LayoutTypes } from '../../constants/layout';

import { LocationsContextType } from './types';
import { Location } from '../../helpers/api/types';
import CenteredSpinner from '../../components/Loaders/CenteredSpinner';
import { changeBodyAttribute } from './utils';
import LocationsContext from '../../context/LocationsContext';

const Topbar = React.lazy(() => import('./Topbar'));
const Navbar = React.lazy(() => import('./Navbar'));
const Footer = React.lazy(() => import('../Footer'));

type HorizontalLayoutProps = {
  children: React.ReactNode;
};

const HorizontalLayout = ({ children }: HorizontalLayoutProps) => {
  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);
  const [locationsData, setLocationsData] = useState<Location[]>([]);
  const [selectedLocationData, setSelectedLocationData] = useState<Location>({} as Location);

  const { menuPosition, topbarTheme } = useSelector((state: RootState) => ({
    menuPosition: state.Layout.menuPosition,
    topbarTheme: state.Layout.topbarTheme,
  }));

  useEffect(() => {
    changeBodyAttribute('data-layout-mode', LayoutTypes.LAYOUT_HORIZONTAL);
  }, []);

  useEffect(() => {
    changeBodyAttribute('data-layout-menu-position', menuPosition);
  }, [menuPosition]);

  useEffect(() => {
    changeBodyAttribute('data-topbar-color', topbarTheme);
  }, [topbarTheme]);

  const setLocations = (locations: Location[], resetLocations?: boolean) => {
    if (resetLocations) {
      setLocationsData([]);
    }
    setLocationsData(locations);
  };

  const toggleMenu = () => {
    const newMenuState = !isMenuOpened;
    setIsMenuOpened(newMenuState);

    if (document.body) {
      document.body.classList.toggle('sidebar-enable', newMenuState);
    }
  };

  const locationsContextValue: LocationsContextType = {
    locations: locationsData,
    setLocations,
    selectedLocation: selectedLocationData,
    setSelectedLocation: setSelectedLocationData,
  };

  return (
    <div id="wrapper" className="d-flex flex-column min-vh-100">
      <Suspense fallback={<CenteredSpinner color="primary" />}>
        <Topbar openLeftMenuCallBack={toggleMenu} />
      </Suspense>

      <LocationsContext.Provider value={locationsContextValue}>
        <Suspense fallback={<CenteredSpinner color="primary" />}>
          <Navbar isMenuOpened={isMenuOpened} />
        </Suspense>

        <div className="content-page flex-grow-1">
          <div className="content">
            <Container fluid>
              <Suspense fallback={<CenteredSpinner color="primary" />}>{children}</Suspense>
            </Container>
          </div>

          <Suspense fallback={<CenteredSpinner color="primary" />}>
            <Footer />
          </Suspense>
        </div>
      </LocationsContext.Provider>
    </div>
  );
};

export default HorizontalLayout;
